import { useEffect, useRef, useState } from "react";

const STEPS = [
  {
    title: "We review and follow up",
    description:
      "Within two business days, we follow up with a few questions about your team and goals.",
  },
  {
    title: "30-minute call",
    description:
      "If there's a fit, we schedule a call to understand your workflows and what you want to change.",
  },
  {
    title: "Programme proposal",
    description:
      "We send a proposal within five days of the call. No commitment required to see it.",
  },
];

const LINE_TRACK = "#08090A1F";
const LINE_FILL = "#08090A";
const SEGMENT_DURATION_MS = 1500;
const SECTION_VISIBILITY_THRESHOLD = 0.5;

const StepBadge = ({ index, active }) => (
  <span
    className={`inline-flex items-center justify-center rounded-[2px] px-3 py-1 text-[12px] font-medium transition-colors duration-700 ${
      active
        ? "bg-black text-white"
        : "border border-[#08090A33] bg-white text-black"
    }`}
  >
    Step {index + 1}
  </span>
);

const BusinessProcess = () => {
  const sectionRef = useRef(null);
  const [animate, setAnimate] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  );
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    if (!animate) return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const timers = STEPS.slice(1).map((_, i) =>
      setTimeout(
        () => setActiveStep(i + 1),
        reduced ? 0 : (i + 1) * SEGMENT_DURATION_MS,
      ),
    );
    return () => timers.forEach(clearTimeout);
  }, [animate]);

  useEffect(() => {
    if (animate) return;
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setAnimate(true);
            observer.disconnect();
            break;
          }
        }
      },
      { threshold: SECTION_VISIBILITY_THRESHOLD },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [animate]);

  return (
    <section
      ref={sectionRef}
      id="business-process"
      data-section-theme="light"
      className="w-full bg-white font-fira-sans"
      style={{
        borderTop: "1px solid #08090A33",
        borderBottom: "1px solid #08090A33",
      }}
    >
      <div
        className="w-[90%] md:w-auto mx-auto md:mx-[153px]"
        style={{
          borderLeft: "1px solid #08090A33",
          borderRight: "1px solid #08090A33",
        }}
      >
        <div className="px-4 md:pl-[60px] md:pr-[60px] py-16">
          <div className="flex flex-col md:flex-row gap-12">
            {/* Left */}
            <div className="md:w-[35%]">
              <p className="text-black uppercase tracking-[1.75px] mb-4">
                THE PROCESS
              </p>
              <h2 className="text-black text-[30px] md:text-[38px] font-medium leading-tight">
                What happens when you enquire?
              </h2>
            </div>

            {/* Right */}
            <ol className="md:w-[65%] flex flex-col">
              {STEPS.map((step, i) => {
                const isLast = i === STEPS.length - 1;
                return (
                  <li
                    key={step.title}
                    className="relative flex items-stretch gap-5 md:gap-6"
                  >
                    <div className="relative flex w-20 flex-col items-center self-stretch">
                      <StepBadge index={i} active={i <= activeStep} />
                      {!isLast && (
                        <span
                          aria-hidden="true"
                          className="relative flex-1 overflow-hidden mt-1"
                          style={{
                            width: "2px",
                            backgroundColor: LINE_TRACK,
                            minHeight: "1rem",
                          }}
                        >
                          <span
                            className="absolute left-0 right-0 top-0 block"
                            style={{
                              backgroundColor: LINE_FILL,
                              height: animate ? "100%" : "0%",
                              transition: `height ${SEGMENT_DURATION_MS}ms ease-in-out`,
                              transitionDelay: `${i * SEGMENT_DURATION_MS}ms`,
                            }}
                          />
                        </span>
                      )}
                    </div>
                    <div className={`flex-1 ${!isLast ? "pb-10 md:pb-14" : ""}`}>
                      <h3 className="text-black text-[18px] md:text-[20px] font-medium leading-tight">
                        {step.title}
                      </h3>
                      <p className="mt-2 text-sm md:text-base text-black/60 leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BusinessProcess;
